import React, { useEffect, useState, useRef } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom/dist";
import { emitter } from "../../config/emitter";
import { api } from "../../api/apiService";
import { getImageUrl, InitialAvatar } from "../../config/utils";

export default function PostLoginLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("role");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [user, setUser] = useState({});
  const [profileImage, setProfileImage] = useState("");
  const [deviceStatus, setDeviceStatus] = useState(null);
  const deviceAlertRef = useRef(null);

  useEffect(() => {
    fetchProfile();

    const handleImage = (data) => {
      setProfileImage(data);
    };

    const updatedUserObject = (data) => {
      setUser(data);
    };

    emitter.on("image", handleImage);
    emitter.on("updatedProfile", updatedUserObject);

    return () => {
      emitter.off("image", handleImage);
      emitter.off("updatedProfile", updatedUserObject);
    };
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prevState) => {
      const newState = !prevState;
      if (newState) {
        document.body.classList.add("toggle-sidebar");
      } else {
        document.body.classList.remove("toggle-sidebar");
      }
      return newState;
    });
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get("profile");
      const { first_name, last_name, email, image, name, role } =
        response.data.data;

      const updatedProfile = {
        name,
        first_name,
        last_name,
        email,
        image: image || null,
        role,
      };

      setUser(updatedProfile);
      setProfileImage(updatedProfile.image);
    } catch (error) {
      console.error(error.response);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.getElementById("sidebar");
      const toggleButton = document.querySelector(".toggle-sidebar-btn");
      if (window.innerWidth > 992) return;

      if (
        sidebar &&
        !sidebar.contains(event.target) &&
        toggleButton &&
        !toggleButton.contains(event.target) &&
        !isSidebarCollapsed
      ) {
        setIsSidebarCollapsed(false);
        document.body.classList.remove("toggle-sidebar");
      }
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchDeviceStatus = async () => {
      try {
        const response = await api.get("/device/status/1");
        const data = response.data?.data ?? response.data;
        setDeviceStatus(data);
      } catch {
        setDeviceStatus({ online: false, message: "Failed to fetch device status.", since_last_beat: null });
      }
    };
    fetchDeviceStatus();
    const interval = setInterval(fetchDeviceStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (deviceStatus?.online === false && deviceAlertRef.current && typeof window.bootstrap !== "undefined") {
      const popover = new window.bootstrap.Popover(deviceAlertRef.current, {
        placement: "bottom",
        trigger: "hover focus",
        title: "Device Offline",
        content: deviceStatus?.message ?? "",
      });
      return () => popover.dispose();
    }
  }, [deviceStatus]);

  return (
    <div id="app">
      <div>
        <header
          id="header"
          className="header fixed-top d-flex align-items-center"
        >
          <div className="d-flex align-items-center justify-content-between">
            <Link
              to="/user/today-summary"
              className="logo d-flex align-items-center"
            >
              <img src="/assets/img/Logo.svg" alt="Logo" />
              {/* <span className="d-none d-lg-block ms-1">Easy Hazri</span> */}
            </Link>
            <i
              className="bi bi-list toggle-sidebar-btn"
              onClick={toggleSidebar}
              style={{ cursor: "pointer" }}
            />
          </div>
          <div className="nav-tabs-custom d-flex">
            <Link
              to="/user/today-summary"
              className={`nav-link ${
                location.pathname === "/user/today-summary" ? "active" : ""
              }`}
            >
              Today's Summary
            </Link>
            <Link
              to="/user/daily-report"
              className={`nav-link ${
                location.pathname === "/user/daily-report" ? "active" : ""
              }`}
            >
              Daily Report
            </Link>
            <Link
              to="/user/weekly-report"
              className={`nav-link ${
                location.pathname === "/user/weekly-report" ? "active" : ""
              }`}
            >
              Weekly Report
            </Link>

            <Link
              to="/user/monthly-summary"
              className={`nav-link ${
                location.pathname === "/user/monthly-summary" ? "active" : ""
              }`}
            >
              Monthly Report
            </Link>

            {deviceStatus?.online === false && (
              <div
                ref={deviceAlertRef}
                className="ms-3 alert alert-danger py-1 px-2 mb-0 align-self-center"
              >
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Device Offline: ({deviceStatus?.since_last_beat})
              </div>
            )}
            {deviceStatus?.online === true && (
              <div className="ms-3 alert alert-success py-1 px-2 mb-0 align-self-center">
                <i className="bi bi-check-circle-fill me-2"></i>
                Device Online
              </div>
            )}

          </div>

          <nav className="header-nav ms-auto">
            <ul className="d-flex align-items-center">
              <li className="nav-item dropdown pe-3">
                <a
                  className="nav-link nav-profile d-flex align-items-center pe-0"
                  href="#"
                  data-bs-toggle="dropdown"
                >
                  <img src={getImageUrl(profileImage)} />
                  <span className="d-none d-md-block dropdown-toggle ps-2" />
                </a>
                <ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow profile">
                  <li className="dropdown-header">
                    <div className="d-flex align-items-center ">
                      <div className="d-flex align-items-center justify-content-center nav-image-bg">
                        {profileImage ? (
                          <img
                            src={getImageUrl(profileImage)}
                            alt={profileImage || "User"}
                            width={40}
                            height={40}
                            className="nav-image"
                          />
                        ) : (
                          <InitialAvatar name={user?.name} />
                        )}
                      </div>
                      <div className="ms-2 text-start d-flex flex-column justify-content-center text-capitalize">
                        <h6 className="lh-1">{user?.name || "User"}</h6>
                        <span className="lh-1">{user?.role}</span>
                      </div>
                    </div>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <a
                      className="dropdown-item d-flex align-items-center c-pointer"
                      onClick={() => navigate("/user/profile")}
                    >
                      <i className="bi bi-person" />
                      <span>Profile</span>
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item d-flex align-items-center c-pointer"
                      onClick={handleLogout}
                    >
                      <i className="bi bi-box-arrow-right" />
                      <span>Logout</span>
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
          </nav>
        </header>
        <aside
          id="sidebar"
          className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}
        >
          <ul className="sidebar-nav" id="sidebar-nav">
            {(role === "admin" || role === "regular") && (
              <li className="nav-item">
                <Link
                  to="/user/today-summary"
                  className={`nav-link ${
                    location.pathname === "/user/today-summary"
                      ? "active"
                      : "collapsed"
                  }`}
                >
                  <i className="bi bi-grid" />
                  <span>Home</span>
                </Link>
              </li>
            )}

            {/* <li className="nav-item">
              <a
                className="nav-link collapsed"
                data-bs-target="#reports"
                data-bs-toggle="collapse"
                href="#"
                aria-expanded="false"
              >
                <i className="bi bi-menu-button-wide"></i>
                <span>Reports</span>
                <i className="bi bi-chevron-down ms-auto"></i>
              </a>
              <ul id="reports" className="nav-content collapse">
                <li className="nav-item">
                  <Link to="/user/attendance-logs" className="nav-link">
                    <i className="bi bi-person-check" />
                    <span>Attendance Logs</span>
                  </Link>
                </li>
                {(role === "admin" || role === "regular") && (
                  <>
                    <li className="nav-item">
                      <Link
                        to="/user/monthly-summary"
                        className="nav-link collapsed"
                      >
                        <i className="bi bi-person" />
                        <span>Monthly Summary</span>
                      </Link>
                    </li>

                    <li className="nav-item">
                      <Link
                        to="/user/employee-details"
                        className="nav-link collapsed"
                      >
                        <i className="bi bi-person" />
                        <span>Employee Details</span>
                      </Link>
                    </li>
                  </>
                )}
              </ul>
            </li> */}
            <li className="nav-item">
              <a
                className={`nav-link ${
                  [
                    "/user/attendance-logs",
                    "/user/monthly-summary",
                    "/user/employee-details",
                  ].includes(location.pathname)
                    ? ""
                    : "collapsed"
                }`}
                data-bs-target="#reports"
                data-bs-toggle="collapse"
                href="#"
                aria-expanded={
                  [
                    "/user/attendance-logs",
                    "/user/monthly-summary",
                    "/user/employee-details",
                  ].includes(location.pathname)
                    ? "true"
                    : "false"
                }
              >
                <i className="bi bi-menu-button-wide"></i>
                <span>Reports</span>
                <i className="bi bi-chevron-down ms-auto"></i>
              </a>

              <ul
                id="reports"
                className={`nav-content collapse ${
                  [
                    "/user/attendance-logs",
                    "/user/monthly-summary",
                    "/user/employee-details",
                  ].includes(location.pathname)
                    ? "show"
                    : ""
                }`}
              >
                <li className="nav-item">
                  <Link
                    to="/user/attendance-logs"
                    className={`nav-link ${
                      location.pathname === "/user/attendance-logs"
                        ? "active"
                        : ""
                    }`}
                  >
                    <i className="bi bi-person-check" />
                    <span>Attendance Logs</span>
                  </Link>
                </li>

                {(role === "admin" || role === "regular") && (
                  <>
                    
                    <li className="nav-item">
                      <Link
                        to="/user/employee-details"
                        className={`nav-link ${
                          location.pathname === "/user/employee-details"
                            ? "active"
                            : ""
                        }`}
                      >
                        <i className="bi bi-person" />
                        <span>Employee Details</span>
                      </Link>
                    </li>
                  </>
                )}
              </ul>
            </li>

            {(role === "admin" || role === "regular") && (
              <>
                <li className="nav-item">
                  <Link
                    to="/user/payrolls"
                    className={`nav-link ${
                      location.pathname === "/user/payrolls"
                        ? "active"
                        : "collapsed"
                    }`}
                  >
                    <i className="bi bi-cash-stack" />
                    <span>Payrolls</span>
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    to="/user/employees"
                    className={`nav-link ${
                      location.pathname === "/user/employees"
                        ? "active"
                        : "collapsed"
                    }`}
                  >
                    <i className="bi bi-person" />
                    <span>Employees</span>
                  </Link>
                </li>

                <li className="nav-item">
                  <a
                    className={`nav-link ${
                      [
                        "/user/settings",
                        "/user/users",
                        "/user/shifts",
                        "/user/departments",
                        "/user/branches",
                        "/user/roles",
                        "/user/change-password",
                      ].includes(location.pathname)
                        ? ""
                        : "collapsed"
                    }`}
                    data-bs-target="#components-nav"
                    data-bs-toggle="collapse"
                    href="#"
                    aria-expanded={
                      [
                        "/user/settings",
                        "/user/users",
                        "/user/shifts",
                        "/user/departments",
                        "/user/branches",
                        "/user/roles",
                        "/user/change-password",
                      ].includes(location.pathname)
                        ? "true"
                        : "false"
                    }
                  >
                    <i className="bi bi-menu-button-wide"></i>
                    <span>Setup</span>
                    <i className="bi bi-chevron-down ms-auto"></i>
                  </a>

                  <ul
                    id="components-nav"
                    className={`nav-content collapse ${
                      [
                        "/user/settings",
                        "/user/users",
                        "/user/shifts",
                        "/user/departments",
                        "/user/branches",
                        "/user/roles",
                        "/user/change-password",
                      ].includes(location.pathname)
                        ? "show"
                        : ""
                    }`}
                    data-bs-parent="#sidebar-nav"
                  >
                    <li className="nav-item">
                      <Link
                        to="/user/settings"
                        className={`nav-link ${
                          location.pathname === "/user/settings"
                            ? "active"
                            : "collapsed"
                        }`}
                      >
                        <i className="bi bi-person" />
                        <span>General Settings</span>
                      </Link>
                    </li>

                    {role === "admin" && (
                      <li className="nav-item">
                        <Link
                          to="/user/users"
                          className={`nav-link ${
                            location.pathname === "/user/users"
                              ? "active"
                              : "collapsed"
                          }`}
                        >
                          <i className="bi bi-person" />
                          <span>Users</span>
                        </Link>
                      </li>
                    )}

                    <li className="nav-item">
                      <Link
                        to="/user/shifts"
                        className={`nav-link ${
                          location.pathname === "/user/shifts"
                            ? "active"
                            : "collapsed"
                        }`}
                      >
                        <i className="bi bi-building" />
                        <span>Shifts</span>
                      </Link>
                    </li>

                    <li className="nav-item">
                      <Link
                        to="/user/departments"
                        className={`nav-link ${
                          location.pathname === "/user/departments"
                            ? "active"
                            : "collapsed"
                        }`}
                      >
                        <i className="bi bi-building" />
                        <span>Departments</span>
                      </Link>
                    </li>

                    <li className="nav-item">
                      <Link
                        to="/user/branches"
                        className={`nav-link ${
                          location.pathname === "/user/branches"
                            ? "active"
                            : "collapsed"
                        }`}
                      >
                        <i className="bi bi-building" />
                        <span>Branches</span>
                      </Link>
                    </li>

                    <li className="nav-item">
                      <Link
                        to="/user/roles"
                        className={`nav-link ${
                          location.pathname === "/user/roles"
                            ? "active"
                            : "collapsed"
                        }`}
                      >
                        <i className="bi bi-person-badge" />
                        <span>Roles</span>
                      </Link>
                    </li>

                    <li className="nav-item">
                      <Link
                        to="/user/change-password"
                        className={`nav-link ${
                          location.pathname === "/user/change-password"
                            ? "active"
                            : "collapsed"
                        }`}
                      >
                        <i className="bi bi-person" />
                        <span>Change Password</span>
                      </Link>
                    </li>
                  </ul>
                </li>
              </>
            )}

            <li className="nav-item">
              <Link
                to="/user/profile"
                className={`nav-link ${
                  location.pathname === "/user/profile" ? "active" : "collapsed"
                }`}
              >
                <i className="bi bi-person" />
                <span>Profile</span>
              </Link>
            </li>

            <li className="nav-item">
              <a
                className="nav-link collapsed"
                style={{ cursor: "pointer" }}
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right" />
                <span>Logout</span>
              </a>
            </li>
          </ul>
        </aside>
        <Outlet />
        <footer id="footer" className="footer">
          <div className="copyright">
            {" "}
            © Copyright{" "}
            <strong>
              <span />
            </strong>
            . All Rights Reserved{" "}
          </div>
        </footer>
      </div>
    </div>
  );
}
